import React, { useState, useEffect } from 'react';
import { motion, useScroll, useMotionValueEvent } from 'framer-motion';
import { Menu, X, Car } from 'lucide-react';
import { Link } from 'react-router-dom';
import { smoothScrollTo } from '../lib/utils';

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { scrollY } = useScroll();

  useMotionValueEvent(scrollY, "change", (latest) => {
    setIsScrolled(latest > 50);
  });

  // Close mobile menu on larger screens
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const navVariants = {
    initial: { opacity: 0, y: -20 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  const mobileMenuVariants = {
    closed: { opacity: 0, x: '100%' },
    open: { opacity: 1, x: 0, transition: { duration: 0.3 } }
  };

  const navItems = [
    { title: 'Features', href: '/features', isPage: true },
    { title: 'Testimonials', href: '/#testimonials' },
    { title: 'Challenges', href: '/#challenges' },
    { title: 'Pricing', href: '/#pricing' },
    { title: 'Mentorship', href: '/mentorship', isPage: true },
    { title: 'CHiP', href: '/chip', isPage: true }
  ];

  const handleNavLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string, isPage: boolean = false) => {
    if (isPage) return; // Don't prevent default for actual page links
    
    e.preventDefault();
    
    // Close mobile menu if open
    if (isOpen) {
      setIsOpen(false);
    }
    
    // Extract the ID from the href
    const targetId = href.replace('/#', '');
    
    // Scroll to the target section
    smoothScrollTo(targetId);
  };

  return (
    <motion.header 
      className={`fixed top-0 left-0 right-0 z-[9000] transition-all duration-300 ${
        isScrolled ? 'bg-background/90 backdrop-blur-sm shadow-lg' : 'bg-transparent'
      }`}
      initial="initial"
      animate="animate"
      variants={navVariants}
      style={{ isolation: 'isolate' }}
    >
      <div className="container mx-auto px-4 py-4 md:py-6 flex justify-between items-center">
        <Link to="/" className="flex items-center space-x-3">
          <Car className="w-8 h-8 text-accent" />
          <span className="text-xl font-extrabold">DetailerOS</span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center space-x-10">
          {navItems.map((item, index) => (
            <motion.div key={index}>
              {item.isPage ? (
                <Link 
                  to={item.href} 
                  className="text-sm font-medium hover:text-accent transition-colors"
                >
                  {item.title}
                </Link>
              ) : (
                <motion.a
                  href={item.href}
                  className="text-sm font-medium hover:text-accent transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={(e) => handleNavLinkClick(e, item.href)}
                >
                  {item.title}
                </motion.a>
              )}
            </motion.div>
          ))}
        </nav>

        <div className="hidden md:flex items-center space-x-4">
          <motion.a
            href="#login"
            className="text-sm font-medium hover:text-accent"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Log in
          </motion.a>
          <motion.a
            href="#signup"
            className="btn btn-primary"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={(e) => handleNavLinkClick(e, '#signup')}
          >
            Get Started
          </motion.a>
        </div>

        {/* Mobile Menu Button */}
        <button 
          className="md:hidden text-white"
          onClick={() => setIsOpen(!isOpen)}
        >
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      <motion.div
        className="md:hidden fixed inset-0 z-[9001] bg-background/95 backdrop-blur-md pt-20"
        initial="closed"
        animate={isOpen ? "open" : "closed"}
        variants={mobileMenuVariants}
        style={{ isolation: 'isolate' }}
      >
        <div className="flex flex-col items-center space-y-8 p-8">
          {navItems.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 * index }}
            >
              {item.isPage ? (
                <Link 
                  to={item.href} 
                  className="text-xl font-medium hover:text-accent"
                  onClick={() => setIsOpen(false)}
                >
                  {item.title}
                </Link>
              ) : (
                <motion.a
                  href={item.href}
                  className="text-xl font-medium hover:text-accent"
                  onClick={(e) => handleNavLinkClick(e, item.href)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {item.title}
                </motion.a>
              )}
            </motion.div>
          ))}
          <div className="flex flex-col space-y-4 w-full items-center pt-6">
            <motion.a
              href="#login"
              className="text-xl font-medium hover:text-accent w-full text-center"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              Log in
            </motion.a>
            <motion.a
              href="#signup"
              className="btn btn-primary w-full text-center"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              onClick={(e) => handleNavLinkClick(e, '#signup')}
            >
              Get Started
            </motion.a>
          </div>
        </div>
      </motion.div>
    </motion.header>
  );
};

export default Navbar;